/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { useGetValueListByIdQuery } from '@api/value-list';
import { skipToken } from '@reduxjs/toolkit/query';
import { loggerMessage } from '../logger';
import { useGetInitiativeUniversalTrackersQuery } from '@api/initiative-universal-trackers';
import { MergedValueListOption, ValueListConfig } from '@g17eco/types/initiativeUniversalTracker';
import { checkIsValueListMetric } from '@utils/universalTracker';

export const useUtrValueList = (
  utr: Pick<UniversalTrackerPlain, '_id' | 'valueType' | 'valueValidation'>,
  initiativeId: string,
): ValueListConfig<MergedValueListOption> | undefined => {
  const { valueList } = utr.valueValidation ?? {};

  const { data: initiativeUtrs, isFetching: isFetchingInitiativeUtrs } = useGetInitiativeUniversalTrackersQuery(
    initiativeId,
    {
      // Use initiative api for metrics that support override value list options (ValueList, ValueListMulti, Table).
      skip: !checkIsValueListMetric(utr),
    },
  );

  const valueListConfig = initiativeUtrs?.find((iUtr) => iUtr.universalTrackerId === utr._id)?.valueListConfig;

  const { data: valueListData, error: valueListError } = useGetValueListByIdQuery(valueList?.listId ?? skipToken, {
    skip:
      Array.isArray(valueList?.list) ||
      valueList?.type === 'custom' ||
      isFetchingInitiativeUtrs ||
      Boolean(valueListConfig),
  });

  // If valueListConfig is available, use it. Otherwise, fallback to normal flow: getting value list for non-table metrics, TableInputView will handle table metrics value list separately.
  if (valueListConfig) {
    return valueListConfig;
  }

  if (!valueList) {
    return;
  }

  const { custom, type } = valueList;

  if (type === 'list' && Array.isArray(valueList.list)) {
    // Already have list available, not need to fetch
    return { options: valueList.list };
  }

  if (type === 'custom') {
    return Array.isArray(custom) ? { options: custom } : undefined;
  }

  if (valueListError) {
    loggerMessage(valueListError.message ?? 'Failed to fetch value list', {
      listId: valueList.listId,
      utrId: utr._id,
    });
  }

  return valueListData;
};
